import React, { useState, useRef } from "react";
import { Plus } from "lucide-react";
import { motion } from "framer-motion";
import { useMouseParallax } from "../../utils/mouseParallax";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Button from "../ui/Button";

const faqData = [
    {
        question:
            "Quelles sont les étapes de création d'un site web professionnel ?",
        answer: "Notre processus de développement web comprend 4 phases essentielles : 1) Analyse de vos besoins et objectifs, 2) Conception UX/UI avec maquettes responsive, 3) Développement front-end et back-end avec React, Vue.js ou WordPress, 4) Tests et mise en ligne. Chaque site est créé sur mesure, optimisé pour le référencement Google et parfaitement adapté aux appareils mobiles.",
    },
    {
        question:
            "Combien de temps faut-il pour développer un site web ou une application ?",
        answer: "Les délais varient selon la complexité du projet : 2-4 semaines pour un site vitrine, 4-8 semaines pour un site e-commerce ou application web, et 6-10 semaines pour une application mobile. Notre équipe de développeurs établit un planning précis avec des jalons pour respecter vos échéances. La transparence sur les délais de livraison est primordiale pour nous.",
    },
    {
        question:
            "Comment optimisez-vous le référencement (SEO) de vos développements web ?",
        answer: "Notre approche SEO intègre : code HTML5 sémantique, vitesse de chargement optimisée (<2s), responsive design, et structure de données structurées. Nos développeurs web implémentent les balises meta optimisées, créent une architecture favorable au référencement Google et assurent la compatibilité mobile, facteur essentiel pour le positionnement dans les résultats de recherche.",
    },
    {
        question:
            "Quelles technologies utilisez-vous pour le développement web et mobile ?",
        answer: "Nous maîtrisons les technologies modernes : React.js, Vue.js et Next.js pour les sites dynamiques, WordPress pour les sites évolutifs, WooCommerce et Shopify pour l'e-commerce. Pour le développement mobile, nous utilisons React Native et Flutter pour le cross-platform, Swift (iOS) et Kotlin (Android) pour les applications natives. Notre équipe full-stack intègre également des API RESTful pour des solutions web performantes.",
    },
    {
        question:
            "Proposez-vous des sites web responsive adaptés à tous les appareils ?",
        answer: "Absolument ! Tous nos développements suivent l'approche mobile-first. Nos sites web s'adaptent automatiquement à tous les écrans (smartphones, tablettes, ordinateurs). Nous utilisons des frameworks comme React, Vue.js et Bootstrap pour garantir une expérience utilisateur optimale sur tous les appareils. Chaque projet est testé sur différentes tailles d'écran avant livraison.",
    },
    {
        question:
            "Développez-vous des applications spécifiques pour iOS et Android ?",
        answer: "Oui, notre équipe développe des applications mobiles natives (Swift pour iOS, Kotlin pour Android) et cross-platform (React Native, Flutter). Nous créons des applications performantes, intuitives et parfaitement intégrées aux fonctionnalités des appareils mobiles. Chaque application est optimisée pour les stores d'applications avec un accent sur l'expérience utilisateur et les performances.",
    },
    {
        question:
            "Quelle est votre approche en design UX/UI pour les interfaces web et mobile ?",
        answer: "Notre méthode de design UX/UI commence par l'analyse des besoins utilisateurs et la création de wireframes interactifs. Nos designers créent ensuite des maquettes responsive avec Figma, respectant les principes d'accessibilité. Chaque interface est conçue pour maximiser l'engagement, avec une attention particulière à l'ergonomie mobile et à votre identité visuelle. Le design centré utilisateur est au cœur de notre approche.",
    },
    {
        question:
            "Pouvez-vous créer ou refondre une boutique e-commerce complète ?",
        answer: "Absolument ! Nous développons des boutiques e-commerce performantes avec WooCommerce, Shopify ou des solutions sur mesure. Nos développements incluent : catalogue produits, paniers d'achat optimisés, passerelles de paiement sécurisées, gestion des stocks et intégration CRM. Chaque site e-commerce est conçu pour maximiser les conversions et offrir une expérience d'achat fluide sur tous les appareils.",
    },
    {
        question:
            "Proposez-vous des fonctionnalités personnalisées pour les sites web ?",
        answer: "Oui, nous développons des fonctionnalités web sur mesure : systèmes de réservation, intégrations API, tableaux de bord personnalisés, chatbots, configurateurs de produits et plus encore. Notre équipe de développeurs full-stack crée des solutions adaptées exactement à vos besoins métier, avec une attention particulière à l'expérience utilisateur et à la performance technique.",
    },
];

const FAQ = () => {
    const [activeIndex, setActiveIndex] = useState(null);
    const sectionRef = useRef(null);
    const location = useLocation();
    const navigate = useNavigate();

    useMouseParallax({ elementRef: sectionRef });

    const toggleAccordion = (index) => {
        setActiveIndex(activeIndex === index ? null : index);

        // Scroll vers la question avec Tailwind uniquement
        if (activeIndex !== index) {
            setTimeout(() => {
                const faqContainer = document.querySelector(".faq-container");
                const questionElement = document.getElementById(
                    `faq-question-${index}`
                );

                if (faqContainer && questionElement) {
                    // Utilisation de scrollTo sans styles personnalisés
                    faqContainer.scrollTo({
                        top: questionElement.offsetTop - 20,
                        behavior: "smooth",
                    });
                }
            }, 50);
        }
    };

    return (
        <section
            id="faq"
            className="pb-20 pt-5 md:pt-40 relative"
            ref={sectionRef}
        >
            <div className="absolute top-40 left-40 w-80 h-80 rounded-full bg-orange-200/20 blur-3xl floating-element"></div>
            <div className="absolute bottom-20 right-1/4 w-64 h-64 rounded-full bg-blue-200/20 blur-2xl floating-element"></div>

            <div className="container mx-auto px-4 relative z-10">
                <div className="flex mx-auto justify-center items-center">
                    <div className="mb-16 max-w-2xl text-center">
                        <div className="mb-6 mt-4">
                            {/* FAQ tag commented out */}
                        </div>
                        <div>
                            <motion.h2
                                className="text-4xl md:text-5xl font-bold text-center title"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.2 }}
                            >
                                Questions{" "}
                                <span className="text-gradient">
                                    fréquemment
                                </span>{" "}
                                posées
                            </motion.h2>
                        </div>
                    </div>
                </div>

                <div className="max-w-6xl mx-auto w-full">
                    <div className="lg:col-span-2 p-4 md:p-8">
                        <div className="faq-container overflow-y-auto pr-2 space-y-4 md:h-[750px] lg:h-[850px] xl:h-[950px] scrollbar-thin scrollbar-thumb-gray-200 scrollbar-track-transparent hover:scrollbar-thumb-gray-300 overflow-hidden">
                            {faqData.map((item, index) => (
                                <motion.div
                                    id={`faq-question-${index}`}
                                    key={index}
                                    className={`bg-white rounded-xl shadow-sm overflow-hidden transition-all duration-500 ${
                                        activeIndex === index
                                            ? "shadow-md bg-gradient-to-r from-blue-50/30 to-purple-50/30"
                                            : "hover:shadow-md"
                                    }`}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{
                                        delay: 0.3 + index * 0.1,
                                    }}
                                >
                                    <button
                                        className="flex justify-between items-center w-full p-5 text-left focus:outline-none"
                                        onClick={() => toggleAccordion(index)}
                                        aria-expanded={activeIndex === index}
                                        aria-controls={`faq-content-${index}`}
                                    >
                                        <span
                                            className={`text-lg font-medium mr-3 transition-colors duration-300 ${
                                                activeIndex === index
                                                    ? "text-gradient"
                                                    : "text-gray-900"
                                            }`}
                                        >
                                            {item.question}
                                        </span>
                                        <span
                                            className={`flex-shrink-0 transition-transform duration-300 ease-in-out ${
                                                activeIndex === index
                                                    ? "rotate-45 scale-110"
                                                    : ""
                                            }`}
                                        >
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width={20}
                                                height={20}
                                                viewBox="0 0 24 24"
                                            >
                                                {activeIndex === index ? (
                                                    <>
                                                        <defs>
                                                            <linearGradient
                                                                id={`gradPlus-${index}`}
                                                                x1="0%"
                                                                y1="0%"
                                                                x2="100%"
                                                                y2="0%"
                                                            >
                                                                <stop
                                                                    offset="0%"
                                                                    stopColor="#3b82f6"
                                                                />
                                                                <stop
                                                                    offset="50%"
                                                                    stopColor="#8b5cf6"
                                                                />
                                                                <stop
                                                                    offset="100%"
                                                                    stopColor="#ec4899"
                                                                />
                                                            </linearGradient>
                                                        </defs>
                                                        <path
                                                            d="M12 5v14M5 12h14"
                                                            fill="none"
                                                            stroke={`url(#gradPlus-${index})`}
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                        />
                                                    </>
                                                ) : (
                                                    <path
                                                        d="M12 5v14M5 12h14"
                                                        fill="none"
                                                        stroke="#9ca3af"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                    />
                                                )}
                                            </svg>
                                        </span>
                                    </button>

                                    <div
                                        id={`faq-content-${index}`}
                                        className={`overflow-hidden transition-all duration-500 ease-in-out ${
                                            activeIndex === index
                                                ? "max-h-[500px] opacity-100"
                                                : "max-h-0 opacity-0"
                                        }`}
                                    >
                                        <div className="p-5 pt-0">
                                            <p className="text-gray-600">
                                                {item.answer}
                                            </p>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default FAQ;
